<template>
  <BaseModal
    :model-value="isOpen"
    :title="$t('auth.security.stepUp.title')"
    :description="$t('auth.security.stepUp.description')"
    max-width-class="max-w-md"
    :close-on-outside-click="!submitting"
    center-on-mobile
    @update:model-value="onClose"
    @close="onClose"
  >
    <AppForm
      :problem="problem"
      class="flex flex-col gap-4"
      @submit="onSubmit"
    >
      <div
        v-if="useRecovery"
        class="flex flex-col gap-1.5"
      >
        <RecoveryCodeField
          ref="recoveryInput"
          :model-value="recoveryCodeField.state.value"
          :label="$t('auth.security.stepUp.recoveryLabel')"
          name="recovery_code"
          :disabled="submitting"
          :placeholder="$t('auth.security.stepUp.recoveryPlaceholder')"
          :error="getFieldError('recoveryCode')"
          :server-error="errorMessage ?? ''"
          @update:model-value="onRecoveryUpdate"
          @blurred="recoveryCodeField.api.handleBlur"
        />
      </div>

      <div
        v-else
        class="flex flex-col gap-1.5"
      >
        <VerificationField
          ref="codeInput"
          :model-value="codeField.state.value"
          :length="6"
          autofocus
          :label="$t('auth.security.stepUp.codeLabel')"
          name="code"
          :disabled="submitting"
          :error="getFieldError('code')"
          :server-error="errorMessage ?? ''"
          @update:model-value="onCodeUpdate"
        />
      </div>

      <button
        type="button"
        class="cursor-pointer font-medium hover:underline self-start text-primary text-sm"
        :disabled="submitting"
        @click="toggleRecovery"
      >
        {{
          useRecovery ? $t('auth.security.stepUp.useCode') : $t('auth.security.stepUp.useRecovery')
        }}
      </button>
    </AppForm>

    <template #footer>
      <button
        type="button"
        class="border border-border-level-200 cursor-pointer dark:border-border-inverse/10 dark:hover:bg-surface-level-800 dark:text-content-level-300 font-medium hover:bg-surface-level-100 px-4 py-2 rounded-lg text-content-level-700 text-sm"
        :disabled="submitting"
        @click="onClose"
      >
        {{ $t('common.actions.cancel') }}
      </button>

      <button
        type="button"
        class="bg-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 font-medium hover:bg-primary/90 px-4 py-2 rounded-lg text-content-inverse text-sm"
        :disabled="submitting || !isValid"
        @click="onSubmit"
      >
        {{ submitting ? $t('auth.security.stepUp.verifying') : $t('auth.security.stepUp.verify') }}
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import AppForm from '~/design-system/molecules/AppForm.molecule.vue'
import { nextTick, useTemplateRef, watch } from 'vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import RecoveryCodeField from '~/design-system/molecules/RecoveryCodeField.molecule.vue'
import VerificationField from '~/design-system/molecules/VerificationField.molecule.vue'
import { useStepUp } from '~/composables/useStepUp'
import { useStepUpCredentialForm } from '~/composables/useStepUpCredentialForm'
import { useTwoFactorTestAssist } from '~/composables/useTwoFactorTestAssist'

const { isOpen, submitting, errorMessage, submit, cancel } = useStepUp()

const {
  codeField,
  getFieldError,
  handleSubmit,
  isValid,
  onCodeUpdate,
  onRecoveryUpdate,
  problem,
  recoveryCodeField,
  resetCredentials,
  useRecovery
} = useStepUpCredentialForm(submit)

const { fillStepUpCode } = useTwoFactorTestAssist()

const codeInput = useTemplateRef<{ focus?: () => void }>('codeInput')
const recoveryInput = useTemplateRef<{ focus?: () => void }>('recoveryInput')

const toggleRecovery = async () => {
  resetCredentials(!useRecovery.value)

  await nextTick()
  ;(useRecovery.value ? recoveryInput.value : codeInput.value)?.focus?.()
}

const onSubmit = async () => {
  if (submitting.value) return
  await handleSubmit()
}

const onClose = () => {
  if (submitting.value) return
  cancel()
}

// Each opening gets an id; a code response from an earlier opening is discarded.
let opening = 0

// Reset local input each time the prompt opens so a prior attempt never leaks in.
watch(isOpen, async (open) => {
  opening += 1
  if (!open) return
  const current = opening
  resetCredentials(false)
  await nextTick()
  codeInput.value?.focus?.()
  await fillStepUpCode((code) => {
    if (current === opening && !useRecovery.value && codeField.state.value === '') {
      onCodeUpdate(code)
    }
  })
})
</script>
